import { action, configure, observable, makeObservable } from 'mobx';
import { observer, Provider } from 'mobx-react';
import * as React from 'react';

import './styles/base';
import './styles/field';
import './styles/responsive';

import { getLocale } from '../utils/pathHelper';

import locales, { ITranslation } from './locales';
import ByAccess from './stores/ByAccess';
import ByArea from './stores/ByArea';
import ByPrefecture from './stores/ByPrefecture';

import fixedDataAreas from './fixedData/fixedDataAreas';
import fixedDataPrefectures from './fixedData/fixedDataPrefectures';

import { IArea } from './models/area';
import { ILine } from './models/line';
import { IPrefecture } from './models/prefecture';
import { IResort } from './models/resort';

import SearchByAccess from './components/byAccess/SearchByAccess';
// import SearchByArea from './components/byArea/SearchByArea';
// import SearchByPrefecture from './components/byPrefecture/SearchByPrefecture';
// import TabNavigation from './components/TabNavigation';

configure({ isolateGlobalState: true });

export interface Iui {
  selected: string;
  byAccess: ByAccess,
  byArea: ByArea,
  byPrefecture: ByPrefecture,
}

interface IResortSearchUI {
  showMap?: boolean;
}

const byAccess = new ByAccess();
const byArea = new ByArea();
const byPrefecture = new ByPrefecture();

@observer
class ResortSearchUI extends React.Component<IResortSearchUI, any> {

  @observable ui: Iui = {
    selected: 'access',
    byAccess,
    byArea,
    byPrefecture,
  }
  @observable t: ITranslation = locales[getLocale() || 'en']

  constructor(props: any) {
    super(props);
    makeObservable(this);
    this.ui.byAccess.showMap = this.props.showMap || false;
    this.ui.byAccess.update = this.updateByAccess;
    this.ui.byAccess.updateAccess = this.updateAccess;
    this.ui.byPrefecture.update = this.updateByPrefecture;
    this.ui.byPrefecture.fetchPrefectures = this.fetchPrefectures;
    this.ui.byArea.update = this.updateByArea;
    this.ui.byArea.fetchAreas = this.fetchAreas;
  }

  @action.bound
  private handleClick(e: any) {
    this.ui.selected = e.target.dataset.name;
  }

  @action.bound
  public updateByAccess() {
    const { byAccess } = this.ui;
    const origin = byAccess.currentOrigin;
    byAccess.isLoading = true;
    fetch(`/snow/api/v1/access/resorts?place_id=${origin.place_id}&duration=${byAccess.duration}`)
      .then((data) => {
        return data.json();
      })
      .then(({ resorts, error }: { resorts: IResort[], error?: string[] }) => {
        if (error) { this.consoleError(error); }
        this.ui.byAccess.resorts = resorts;
        byAccess.isLoading = false;
      })
      .catch((error) => {
        this.consoleError(error);
        byAccess.isLoading = false;
      });
  }

  @action.bound
  public updateAccess({ origin_id, dest_id }: { origin_id: string, dest_id: string }): void {
    const defaultAccess = { path: [], duration: null };
    const { byAccess } = this.ui;
    const { access } = byAccess;
    byAccess.access = defaultAccess;
    byAccess.isLoading = true;
    fetch(`/snow/api/v1/accesses/by_duration_and_interchange/${origin_id},${dest_id}`)
      .then((data) => {
        return data.json();
      })
      .then(({ accesses, error }: { accesses: Array<{ path: ILine[], duration: number }>, error?: string[] }) => {
        if (error) { this.consoleError(error); }
        byAccess.access = accesses[0];
        byAccess.isLoading = false;
      })
      .catch((error) => {
        console.error(error);
        byAccess.access = defaultAccess;
        byAccess.isLoading = false;
      });
  }

  @action.bound
  public fetchPrefectures(callback: () => void) {
    const { byPrefecture } = this.ui;
    byPrefecture.isLoading = true;
    fetch('/snow/api/v1/prefectures').then(data => data.json())
      .then(({ prefectures, error }: { prefectures: IPrefecture[], error?: string[] }) => {
        if (error) { this.consoleError(error); }
        byPrefecture.prefectures = prefectures;
        byPrefecture.isLoading = false;
        callback();
      })
      .catch((error) => {
        console.error(error);
        byPrefecture.prefectures = fixedDataPrefectures;
        byPrefecture.isLoading = false;
        callback();
      });
  }

  @action.bound
  public updateByPrefecture() {
    const { byPrefecture } = this.ui;
    const { currentPrefecture } = byPrefecture;
    if (currentPrefecture == null) { return; }
    byPrefecture.isLoading = true;
    fetch(`/snow/api/v1/prefecture/${currentPrefecture.code}/resorts`)
      .then((data) => {
        return data.json();
      })
      .then(({ resorts, error }: { resorts: IResort[], error?: string[] }) => {
        if (error) { this.consoleError(error); }
        byPrefecture.resorts = resorts;
        byPrefecture.isLoading = false;
      })
      .catch((error) => {
        console.error(error);
        byPrefecture.isLoading = false;
      });
  }

  @action.bound
  public fetchAreas(callback: () => void) {
    const { byArea } = this.ui;
    byArea.isLoading = true;
    fetch('/snow/api/v1/areas').then(data => data.json())
      .then(({ areas, error }: { areas: IArea[], error?: string[] }) => {
        if (error) { this.consoleError(error); }
        byArea.areas = areas;
        byArea.isLoading = false;
        callback();
      })
      .catch((error) => {
        console.error(error);
        byArea.areas = fixedDataAreas;
        byArea.isLoading = false;
        callback();
      });
  }

  @action.bound
  public updateByArea() {
    const { byArea } = this.ui;
    const { currentArea } = byArea;
    if (currentArea == null) { return; }
    byArea.isLoading = true;
    fetch(`/snow/api/v1/area/${currentArea.code}/resorts`)
      .then((data) => {
        return data.json();
      })
      .then(({ resorts, error }: { resorts: IResort[], error?: string|string[] }) => {
        if (error) { this.consoleError(error); }
        byArea.resorts = resorts;
        byArea.isLoading = false;
      })
      .catch((error) => {
        console.error(error);
        byArea.isLoading = false;
      });
  }

  public consoleError(error: string|string[]) {
    console.error('ResortSearchUI', error);
  }

  public render() {
    const { selected } = this.ui;
    return (
      <Provider ui={this.ui} t={this.t}>
        <div className="resort-search-container">
          {/*<TabNavigation />*/}
          <div className="resort-search__main">
            {selected === 'access' && <SearchByAccess />}
            {/* selected === 'area' && <SearchByArea /> */}
            {/* selected === 'prefecture' && <SearchByPrefecture /> */}
          </div>
        </div>
      </Provider>
    );
  }
}

export default ResortSearchUI;
